import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Loader2, X, Save, GraduationCap, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/utils/datetime";

type Exam = {
    id: string; title: string; type: "individual" | "group";
    group_id: string | null; student_id: string | null;
    scheduled_at: string; max_score: number; pass_score: number; weight: number;
    status: string;
};

export default function Exams() {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    const nav = useNavigate();
    const [items, setItems] = useState<Exam[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [showNew, setShowNew] = useState(false);
    const [q, setQ] = useState("");
    const [deleteFor, setDeleteFor] = useState<Exam | null>(null);
    const canCreate = hasPerm("exam.create");
    const canDelete = hasPerm("exam.delete");
    const load = async () => {
        setLoading(true); setErr(null);
        try {
            const params = new URLSearchParams(); if (q) params.set("q", q);
            const { data } = await api.get(`/exams?${params}`);
            setItems(data.items);
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [q]);
    return (
        <div className="space-y-6" data-testid="exams-page">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{t("nav.exams")}</h1>
                    <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{items.length}</p>
                </div>
                {canCreate && <button data-testid="exam-new-btn" onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}><Plus size={16} /> {t("common.new")}</button>}
            </div>
            <input data-testid="exams-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("topbar.search_placeholder")} className="surface-input px-3 py-2 w-full max-w-md text-sm" style={{ minHeight: 40 }} />
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
            <div className="surface-card overflow-hidden">
                {loading ? <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div>
                    : items.length === 0 ? <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }} data-testid="exams-empty">{t("common.empty")}</div>
                    : <table className="w-full text-sm">
                        <thead><tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}>
                            <th className="px-4 py-2 font-medium">{t("exams.title_col")}</th>
                            <th className="px-4 py-2 font-medium">{t("exams.type_col")}</th>
                            <th className="px-4 py-2 font-medium">{t("exams.scheduled_at")}</th>
                            <th className="px-4 py-2 font-medium">{t("common.status")}</th>
                            {canDelete && <th className="px-4 py-2 font-medium w-12" />}
                        </tr></thead>
                        <tbody>{items.map((e) => (
                            <tr key={e.id} onClick={() => nav(`/exams/${e.id}`)} className="border-t cursor-pointer hover:bg-[var(--input)]" style={{ borderColor: "var(--border)" }} data-testid={`exam-row-${e.id}`}>
                                <td className="px-4 py-3 font-medium" style={{ color: "var(--text)" }}>{e.title}</td>
                                <td className="px-4 py-3"><span className="status-pill status-info">{t(`exams.type.${e.type}` as any)}</span></td>
                                <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>{fmtDateTime(e.scheduled_at)}</td>
                                <td className="px-4 py-3"><span className="status-pill status-neutral">{t(`exams.status.${e.status}` as any, { defaultValue: e.status })}</span></td>
                                {canDelete && <td className="px-4 py-3 text-right">
                                    <button data-testid={`exam-delete-${e.id}`} title={t("exams.delete")} onClick={(ev) => { ev.stopPropagation(); setDeleteFor(e); }}
                                        className="inline-flex items-center justify-center rounded-md p-1.5 hover:bg-[var(--error-bg)]" style={{ color: "var(--error-fg)" }}>
                                        <Trash2 size={15} />
                                    </button>
                                </td>}
                            </tr>
                        ))}</tbody>
                    </table>}
            </div>
            {showNew && <NewExam onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
            {deleteFor && <DeleteExam exam={deleteFor} onClose={() => setDeleteFor(null)} onDone={() => { setDeleteFor(null); load(); }} />}
        </div>
    );
}

function DeleteExam({ exam, onClose, onDone }: { exam: Exam; onClose: () => void; onDone: () => void }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const del = async () => {
        setBusy(true);
        try {
            await api.delete(`/exams/${exam.id}`);
            toast.success(t("exams.delete_success"));
            onDone();
        } catch (e: any) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => !busy && onClose()} data-testid="exam-delete-dialog">
            <div className="surface-card w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-lg font-semibold mb-2" style={{ color: "var(--text)" }}>{t("exams.delete_title")}</h2>
                <p className="text-sm mb-1" style={{ color: "var(--text)" }}><strong data-testid="exam-delete-name">{exam.title}</strong></p>
                <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>{t("exams.delete_body")}</p>
                <div className="flex justify-end gap-2">
                    <button data-testid="exam-delete-cancel" disabled={busy} onClick={onClose} className="rounded-md border px-3 py-1.5 text-sm" style={{ borderColor: "var(--border)", color: "var(--text)" }}>{t("common.cancel")}</button>
                    <button data-testid="exam-delete-confirm" disabled={busy} onClick={del} className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium" style={{ background: "var(--error-fg)", color: "#fff" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} {t("exams.delete")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function NewExam({ onClose, onSaved }: any) {
    const { t } = useTranslation();
    const now = new Date(); now.setHours(now.getHours() + 24);
    // Phase UI-2: `weight` is no longer collected from the form. The
    // backend still accepts the field (default 1.0) so existing exams and
    // any external API clients keep working; the tenant UI just does not
    // ask for it and analytics no longer surfaces a weighted average.
    const [state, setState] = useState<any>({
        title: "", type: "group", group_id: "", student_id: "",
        scheduled_at: now.toISOString().slice(0, 16),
        max_score: 100, pass_score: 50, description: "",
    });
    const [groups, setGroups] = useState<any[]>([]);
    const [students, setStudents] = useState<any[]>([]);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    useEffect(() => {
        Promise.all([api.get("/groups"), api.get("/people?type=student&limit=200")])
            .then(([g, p]) => { setGroups(g.data.items); setStudents(p.data.items); });
    }, []);
    const submit = async () => {
        setSaving(true); setErr(null);
        try {
            const payload: any = { ...state,
                scheduled_at: new Date(state.scheduled_at).toISOString(),
                max_score: Number(state.max_score), pass_score: Number(state.pass_score),
            };
            if (payload.type === "group") delete payload.student_id;
            else delete payload.group_id;
            await api.post("/exams", payload);
            onSaved();
        } catch (e: any) {
            const detail = e?.response?.data?.detail;
            const code = typeof detail === "object" ? detail?.error : null;
            const map: Record<string, string> = {
                pass_score_gt_max: t("exams.err.pass_gt_max"),
                group_required: t("exams.err.group_required"),
                student_required: t("exams.err.student_required"),
                group_not_found: t("exams.err.group_not_found"),
                student_not_found: t("exams.err.student_not_found"),
            };
            setErr(map[code || ""] || formatApiError(e));
        } finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="exam-dialog">
            <div className="surface-card w-full max-w-md p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t("common.new")}</h2><button onClick={onClose}><X size={18} style={{ color: "var(--text-muted)" }} /></button></div>
                <div className="space-y-3">
                    <F label={t("exams.title_col")}><input data-testid="exam-title" value={state.title} onChange={(e) => setState({ ...state, title: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></F>
                    <F label={t("exams.type_col")}>
                        <div className="flex gap-2">
                            {(["group", "individual"] as const).map((tp) => (
                                <button key={tp} type="button" data-testid={`exam-type-${tp}`} onClick={() => setState({ ...state, type: tp })}
                                    className="flex-1 rounded-md px-3 py-2 text-xs border"
                                    style={{ background: state.type === tp ? "var(--selected-bg)" : "var(--input)", color: "var(--text)", borderColor: state.type === tp ? "var(--primary)" : "var(--border)" }}>
                                    {t(`exams.type.${tp}`)}
                                </button>
                            ))}
                        </div>
                    </F>
                    {state.type === "group" ? (
                        <F label={t("nav.groups")}>
                            <select data-testid="exam-group" value={state.group_id} onChange={(e) => setState({ ...state, group_id: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }}>
                                <option value="">—</option>
                                {groups.map((g: any) => <option key={g.id} value={g.id}>{g.name}</option>)}
                            </select>
                        </F>
                    ) : (
                        <F label={t("exams.student")}>
                            <select data-testid="exam-student" value={state.student_id} onChange={(e) => setState({ ...state, student_id: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }}>
                                <option value="">—</option>
                                {students.map((s: any) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                            </select>
                        </F>
                    )}
                    <F label={t("exams.scheduled_at")}><input data-testid="exam-date" type="datetime-local" value={state.scheduled_at} onChange={(e) => setState({ ...state, scheduled_at: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></F>
                    <div className="grid grid-cols-2 gap-2">
                        <F label={t("exams.max_score")}><input data-testid="exam-max" type="number" min={1} value={state.max_score} onChange={(e) => setState({ ...state, max_score: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }} /></F>
                        <F label={t("exams.pass_score")}><input data-testid="exam-pass" type="number" min={0} value={state.pass_score} onChange={(e) => setState({ ...state, pass_score: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }} /></F>
                    </div>
                    {err && <div className="text-sm rounded-md p-2" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="exam-err">{err}</div>}
                    <button data-testid="exam-save-btn" disabled={saving || !state.title || (state.type === "group" ? !state.group_id : !state.student_id)} onClick={submit} className="w-full inline-flex justify-center items-center gap-2 rounded-md py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>{saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} {t("common.save")}</button>
                </div>
            </div>
        </div>
    );
}

function F({ label, children }: { label: string; children: React.ReactNode }) {
    return <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>{children}</label>;
}
